/**
 * LoadStepResult Component
 *
 * Displays a single load step result with status, metrics, and SQL operations.
 * Card-based UI matching TransformationStepResult pattern.
 */

import React, { useState } from "react";
import type { QueryLoadResult, SqlOperationLog } from "../../../types/elt-job-execution.types";

interface LoadStepResultProps {
  result: QueryLoadResult;
  index: number;
}

type LoadStepStatus = QueryLoadResult["status"];

const statusConfig: Record<LoadStepStatus, { label: string; className: string; iconColor: string }> = {
  pending: {
    label: "Pending",
    className: "bg-gray-50 border-gray-200",
    iconColor: "text-gray-400",
  },
  creating_table: {
    label: "Creating Table",
    className: "bg-blue-50 border-blue-200",
    iconColor: "text-blue-600",
  },
  cleaning: {
    label: "Cleaning",
    className: "bg-yellow-50 border-yellow-200",
    iconColor: "text-yellow-600",
  },
  loading: {
    label: "Loading",
    className: "bg-blue-50 border-blue-200",
    iconColor: "text-blue-600",
  },
  completed: {
    label: "Completed",
    className: "bg-green-50 border-green-200",
    iconColor: "text-green-600",
  },
  failed: {
    label: "Failed",
    className: "bg-red-50 border-red-200",
    iconColor: "text-red-600",
  },
};

const StatusIcon: React.FC<{ status: LoadStepStatus }> = ({ status }) => {
  const config = statusConfig[status];

  if (status === "completed") {
    return (
      <svg className={`w-5 h-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  }

  if (status === "failed") {
    return (
      <svg className={`w-5 h-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  }

  if (status === "loading" || status === "creating_table" || status === "cleaning") {
    return (
      <svg className={`w-5 h-5 ${config.iconColor} animate-spin`} fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );
  }

  // pending
  return (
    <svg className={`w-5 h-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <circle cx="12" cy="12" r="10" strokeWidth={2} />
    </svg>
  );
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
};

const formatRows = (count: number): string => {
  if (count === -1) return "N/A";
  return count.toLocaleString();
};

const operationLabels: Record<SqlOperationLog["operation"], string> = {
  CREATE_TABLE: "Create Table",
  TRUNCATE: "Truncate",
  DELETE: "Delete",
  INSERT: "Insert",
  DROP_TABLE: "Drop Table",
};

interface SqlOperationRowProps {
  operation: SqlOperationLog;
  index: number;
}

const SqlOperationRow: React.FC<SqlOperationRowProps> = ({ operation, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border-l-2 border-gray-200 pl-3 py-1">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="text-gray-400">#{index + 1}</span>
          <span className="font-medium text-gray-700">{operationLabels[operation.operation] || operation.operation}</span>
          {operation.rowsAffected !== undefined && <span className="text-gray-500">({formatRows(operation.rowsAffected)} rows)</span>}
          {operation.durationMs !== undefined && <span className="text-gray-400">{formatDuration(operation.durationMs)}</span>}
        </div>
        {operation.sql && (
          <button onClick={() => setIsExpanded(!isExpanded)} className="text-blue-600 hover:text-blue-800 text-xs">
            {isExpanded ? "Hide SQL" : "Show SQL"}
          </button>
        )}
      </div>
      {operation.error && <div className="mt-1 text-xs text-red-600 bg-red-50 rounded px-2 py-1">{operation.error}</div>}
      {isExpanded && operation.sql && (
        <div className="mt-2">
          <pre className="text-xs bg-gray-800 text-gray-100 rounded p-2 overflow-x-auto whitespace-pre-wrap font-mono">{operation.sql}</pre>
          {operation.sqlTruncated && <p className="text-xs text-gray-500 mt-1 italic">SQL was truncated due to length limits</p>}
        </div>
      )}
    </div>
  );
};

export const LoadStepResult: React.FC<LoadStepResultProps> = ({ result, index }) => {
  const config = statusConfig[result.status];
  const [showOperations, setShowOperations] = useState(false);

  const hasOperations = result.sqlOperations && result.sqlOperations.length > 0;

  return (
    <div className={`border rounded-lg p-3 ${config.className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-mono w-5">#{index + 1}</span>
          <StatusIcon status={result.status} />
          <div>
            <span className="font-medium text-gray-900">{result.queryName}</span>
            <span className="text-gray-500 mx-1">&rarr;</span>
            <span className="font-mono text-sm text-gray-600">{result.tableName}</span>
          </div>
        </div>
        <span className={`text-xs font-medium uppercase ${config.iconColor}`}>{config.label}</span>
      </div>

      <div className="mt-2 flex items-center gap-4 text-xs text-gray-600 flex-wrap">
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6M12 9v6" />
          </svg>
          {result.rowsLoaded.toLocaleString()} loaded
        </span>
        {result.rowsFailed > 0 && (
          <span className="flex items-center gap-1 text-red-600">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {result.rowsFailed.toLocaleString()} failed
          </span>
        )}
        {result.rowsDeleted !== -1 && result.rowsDeleted > 0 && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {formatRows(result.rowsDeleted)} deleted
          </span>
        )}
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {formatDuration(result.durationMs)}
        </span>
      </div>

      {result.error && (
        <div className="mt-2 p-2 bg-white bg-opacity-60 rounded border border-red-200">
          <p className="text-xs font-medium text-red-700 mb-1">Error:</p>
          <pre className="text-xs text-red-600 whitespace-pre-wrap font-mono overflow-x-auto">{result.error}</pre>
        </div>
      )}

      {hasOperations && (
        <div className="mt-2">
          <button onClick={() => setShowOperations(!showOperations)} className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1">
            <svg className={`w-3 h-3 transition-transform ${showOperations ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showOperations ? "Hide" : "Show"} SQL Operations ({result.sqlOperations!.length})
          </button>
          {showOperations && (
            <div className="mt-2 space-y-1">
              {result.sqlOperations!.map((op, opIndex) => (
                <SqlOperationRow key={opIndex} operation={op} index={opIndex} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
